import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import { createAsyncThunk } from '@/store/create-async-thunk';

import clientAutoCreditSlice from './AutoCredit/auto-credit.store';
import { clientApi, clientCreateApi } from './api';
import { checkIsClientInfoFullyFilled } from './shared/services/helpers/client-store.helpers';
import { isInWebView } from './shared/webview/env';
import { type IUpdateClientInfo } from './types/api.types';
import { type IClient } from './types/client.types';
import {
  type AdditionalPackageInterface,
  type BrandInterface,
  type CityInterface,
  type EquipmentInterface
} from './types/dictionaries.types';
import {
  type ClientStoreInterface,
  type DealerItemResponseInterface
} from './types/store.types';

type TAuthValidateOtpProps = Parameters<
  typeof clientCreateApi.authValidateOtp
>[number];

export const authValidateOtp = createAsyncThunk(
  'client/authValidateOtp',
  async (data: TAuthValidateOtpProps) => {
    const response = await clientCreateApi.authValidateOtp(data);
    return {
      iin: data.iin,
      accessToken: response.accessToken
    };
  }
);

export const getClientInfo = createAsyncThunk(
  'client/getClientInfo',
  async () => clientApi.getClientInfo()
);

// TODO: Возможно придется удалить. Необходимо уточнить необходимость этого
// функционала у аналитиков
export const updateClientInfo = createAsyncThunk(
  'client/updateClientInfo',
  async (data: IUpdateClientInfo) => clientApi.updateClientInfo(data)
);

export const getOnlineDictionaries = createAsyncThunk(
  'client/getOnlineDictionaries',
  async () => clientCreateApi.getOnlineDictionaries()
);

export const getCities = createAsyncThunk(
  'client/getCities',
  (brandId?: string) => clientCreateApi.getAvailableCities(brandId)
);

export const getBrandsByCity = createAsyncThunk(
  'client/getBrandsByCity',
  async (params: { cityId: string; dealerId?: string }, { getState }) => {
    const state = (getState() as { client: ClientStoreInterface }).client;
    if (state.dicts.brands && state.dicts.brands[params.cityId])
      return {
        cityId: params.cityId,
        brands: state.dicts.brands[params.cityId]
      };
    return {
      cityId: params.cityId,
      brands: await clientCreateApi.getBrandsByCity(params)
    };
  }
);

export const getModelsByBrandAndCity = createAsyncThunk(
  'client/getModelsByBrandAndCity',
  async (params: { brandId: string; cityId: string }) => {
    return {
      brandId: params.brandId,
      cityId: params.cityId,
      models: await clientCreateApi.getModelsByBrandAndCity(params)
    };
  }
);

export const getEquipmentsByModel = createAsyncThunk(
  'client/getEquipmentsByModel',
  async (modelId: string, { getState }) => {
    const state = (getState() as { client: ClientStoreInterface }).client;
    if (state.dicts.equipments && state.dicts.equipments[modelId])
      return {
        modelId,
        equipments: state.dicts.equipments[modelId]
      };
    return {
      modelId,
      equipments: await clientCreateApi.getEquipmentsByModel(modelId)
    };
  }
);

export const getAdditionalPackages = createAsyncThunk(
  'client/getAdditionalPackages',
  async (_, { getState }) => {
    const state = (getState() as { client: ClientStoreInterface }).client;
    if (state.dicts.additionalPackages) return state.dicts.additionalPackages;
    return clientCreateApi.getExtraProducts();
  }
);

export const getDealersByCityAndBrand = createAsyncThunk(
  'client/getDealersByCityAndBrand',
  async ({
    filteredQrDealer,
    ...params
  }: {
    cityId: string;
    brandId: string;
    filteredQrDealer: string | null;
  }) => {
    return {
      dealers: await clientCreateApi.getDealersByCityAndBrand(params),
      filteredQrDealer
    };
  }
);

export const getDealerById = createAsyncThunk(
  'client/getDealerById',
  async (dealerId: string) => {
    return clientCreateApi.getDealerById(dealerId);
  }
);

const initialState: ClientStoreInterface = {
  client: null,
  isCityUpdated: false,
  clientAdditionalInfo: {},
  city: null,
  equipmentsData: {
    equipments: []
  },
  dealers: [],
  isAdditionalPackagesLoading: false,
  isBrandsLoading: true,
  isDealersLoaded: false,
  isDealersLoading: false,
  isEquipmentsLoading: false,
  dicts: {},
  theme: 'light',
  isInWebView: isInWebView()
};

const clientSlice = createSlice({
  name: 'client',
  initialState,
  reducers: {
    setClient: (state, action: PayloadAction<IClient | null>) => {
      state.client = action.payload;
    },
    setClientFieldsRequired: (state, action: PayloadAction<boolean>) => {
      state.clientAdditionalInfo.isFieldsRequired = action.payload;
    },
    setClientCity: (state, action: PayloadAction<CityInterface | null>) => {
      state.city = action.payload;
    },
    setIsCityUpdated: (state, action: PayloadAction<boolean>) => {
      state.isCityUpdated = action.payload;
    },
    setEquipmentsData: (
      state,
      action: PayloadAction<typeof initialState.equipmentsData>
    ) => {
      state.equipmentsData = action.payload;
    },
    resetDealers: (state) => {
      state.dealers = [];
    },
    setIsInWebView: (state, action: PayloadAction<boolean>) => {
      state.isInWebView = action.payload;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
    setWebviewProperties: (
      state,
      action: PayloadAction<ClientStoreInterface['webviewProperties']>
    ) => {
      state.webviewProperties = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(authValidateOtp.fulfilled, (state, action) => {
        state.client = { iin: action.payload.iin };
      })
      .addCase(getClientInfo.fulfilled, (state, action) => {
        state.clientAdditionalInfo.initialValues = action.payload;
        state.clientAdditionalInfo.initialValuesFullyFilled =
          checkIsClientInfoFullyFilled(action.payload);
      })
      .addCase(updateClientInfo.fulfilled, (state, action) => {
        state.clientAdditionalInfo.initialValues = action.payload;
        state.clientAdditionalInfo.initialValuesFullyFilled =
          checkIsClientInfoFullyFilled(action.payload);
      })
      .addCase(getOnlineDictionaries.fulfilled, (state, action) => {
        state.dicts = { ...state.dicts, ...action.payload };
      })
      .addCase(getCities.fulfilled, (state, action) => {
        state.dicts.cities = action.payload.sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      })
      .addCase(getBrandsByCity.pending, (state) => {
        state.isBrandsLoading = true;
      })
      .addCase(getBrandsByCity.rejected, (state) => {
        state.isBrandsLoading = false;
      })
      .addCase(
        getBrandsByCity.fulfilled,
        (
          state,
          action: PayloadAction<{ cityId: string; brands: BrandInterface[] }>
        ) => {
          state.dicts.brands ??= {};
          state.dicts.brands[action.payload.cityId] = action.payload.brands.map(
            (brand) => ({
              ...brand,
              name: brand.code === 8 ? 'Li Auto' : brand.name
            })
          );
          state.isBrandsLoading = false;
        }
      )
      .addCase(getModelsByBrandAndCity.fulfilled, (state, action) => {
        state.dicts.models ??= {};
        state.dicts.models[action.payload.brandId] = action.payload.models;
      })
      .addCase(getEquipmentsByModel.pending, (state) => {
        state.isEquipmentsLoading = true;
      })
      .addCase(getEquipmentsByModel.rejected, (state) => {
        state.isEquipmentsLoading = false;
      })
      .addCase(
        getEquipmentsByModel.fulfilled,
        (
          state,
          action: PayloadAction<{
            modelId: string;
            equipments: EquipmentInterface[];
          }>
        ) => {
          state.dicts.equipments ??= {};
          state.dicts.equipments[action.payload.modelId] =
            action.payload.equipments;
          state.isEquipmentsLoading = false;
        }
      )
      .addCase(getAdditionalPackages.pending, (state) => {
        state.isAdditionalPackagesLoading = true;
      })
      .addCase(getAdditionalPackages.rejected, (state) => {
        state.isAdditionalPackagesLoading = false;
      })
      .addCase(
        getAdditionalPackages.fulfilled,
        (state, action: PayloadAction<AdditionalPackageInterface[]>) => {
          state.dicts.additionalPackages = action.payload;
          state.isAdditionalPackagesLoading = false;
        }
      )
      .addCase(getDealersByCityAndBrand.pending, (state) => {
        state.isDealersLoading = true;
        state.isDealersLoaded = false;
        state.dealers = [];
      })
      .addCase(getDealersByCityAndBrand.fulfilled, (state, action) => {
        const { dealers, filteredQrDealer } = action.payload;
        state.isDealersLoading = false;
        state.isDealersLoaded = true;
        state.dealers = dealers
          .filter(
            (item) =>
              item.is_active &&
              (!filteredQrDealer || item.uuid === filteredQrDealer)
          )
          .map((item) => ({
            address: item.physical_address || '—',
            is_active: item.is_active,
            name: item.name || '—',
            uuid: item.uuid,
            workpoint_name: item.workpoint_name || '—'
          }));
      })
      .addCase(getDealersByCityAndBrand.rejected, (state) => {
        state.isDealersLoading = false;
        state.isDealersLoaded = true;
        state.dealers = [];
      })
      .addCase(getDealerById.pending, (state) => {
        state.isDealersLoading = true;
        state.isDealersLoaded = false;
        state.dealers = [];
      })
      .addCase(
        getDealerById.fulfilled,
        (state, action: PayloadAction<DealerItemResponseInterface>) => {
          state.isDealersLoading = false;
          state.isDealersLoaded = true;
          state.dealers = [
            {
              address: action.payload.physical_address || '—',
              is_active: action.payload.is_active,
              name: action.payload.name || '—',
              uuid: action.payload.uuid,
              workpoint_name: action.payload.workpoint_name || '—'
            }
          ];
        }
      )
      .addCase(getDealerById.rejected, (state) => {
        state.isDealersLoading = false;
        state.isDealersLoaded = true;
        state.dealers = [];
      });
  }
});

export const {
  setClient,
  setClientFieldsRequired,
  setClientCity,
  setIsCityUpdated,
  setEquipmentsData,
  resetDealers,
  setTheme,
  setIsInWebView,
  setWebviewProperties
} = clientSlice.actions;

export const clientReducers = {
  client: clientSlice.reducer,
  clientAutoCredit: clientAutoCreditSlice.reducer
};
